import { toImmutable, Immutable } from 'nuclear-js';

import { findAcademicYearForTermNumber } from 'utils/terms';
import { StandardTypes } from 'resources/common-values';
import CommonValues from 'resources/common-values';
import Reactor from 'data/reactor';
import DataUtils from 'data/utils';
import RuntimeGetters from 'data/app-runtime/getters';
import StudentGetters from 'data/student/getters';
import RosterGetters from 'data/roster/getters';
// to prevent circular dependency, the definitions of these have been moved to
//    ./student/utils.js, but they are still exported from this file for backwards compatibility
import {
    findNextStudent,
    findPreviousStudent,
    findTestEventForTermAndSubject,
    findMostRecentTestEventsBySubject,
    ritQuintilesLoaded,
    findMissingRitQuintileKeys,
    buildRitQuintileKeyset,
    districtAveragesLoaded,
    findMissingDistrictAverageKeys,
    buildDistrictAverageKeyset,
    fieldLoadedForStudent,
    growthGoalNormsLoaded,
} from './student/utils.js';

export const findAdjacentStudent = (useTransitionLocation = true) => {
    const studentList = Reactor.evaluate(RosterGetters.students);
    const formerCurrentBid = Reactor.evaluate(RosterGetters.selectedStudentBid);
    const direction = Reactor.evaluate(RuntimeGetters.studentTransitionAction);

    // Normally, we want the student that is transitioning in.
    // In the Student store, we want the one that is transitioning out.
    const locationOfInterest = useTransitionLocation ? 'left' : 'right';

    const adjacentStudent =
        direction == null
            ? null
            : direction == locationOfInterest
            ? findNextStudent(studentList, formerCurrentBid)
            : findPreviousStudent(studentList, formerCurrentBid);
    return adjacentStudent;
};

export const growthGoalsLoadedForStudent = (studentList, studentBid, subjects, checkForTextBid = false) => {
    if (!studentList || !fieldLoadedForStudent(studentList, studentBid, 'growthGoals')) {
        return false;
    }
    /*
        This utility is sometimes called using data that has been pulled from the browser cache (sessionStorage).
        When pulling from cache, numeric keys are returned as strings, which causes the 'hasIn' method to fail.
        Code was added to check for the string value of the subject BID, but only when designated in the function call.
     */
    return subjects.reduce((currVal, nextSubject) => {
        return (
            currVal &&
            (studentList.hasIn([studentBid, 'growthGoals', nextSubject.get('bid')]) ||
                (checkForTextBid && studentList.hasIn([studentBid, 'growthGoals', '' + nextSubject.get('bid')])))
        );
    }, true);
};

export const latestItemByTerm = (itemWithTerm) => {
    // Assumes that items in the array/list have a property called 'termNumber'
    let latestItem = null;
    if (!itemWithTerm) {
        // Nothing was passed in (null or undefined)
        return null;
    } else if (itemWithTerm instanceof Array) {
        itemWithTerm.forEach((item) => {
            if (item.termNumber && (latestItem == null || item.termNumber > latestItem.termNumber)) {
                latestItem = item;
            }
        });
        return latestItem;
    } else if (itemWithTerm instanceof Immutable.List) {
        itemWithTerm.forEach((item) => {
            if (
                item.get('termNumber') &&
                (latestItem == null || item.get('termNumber') > latestItem.get('termNumber'))
            ) {
                latestItem = item;
            }
        });
        return latestItem;
    } else {
        throw new Error(
            'unexpected condition: item supplied should be an array or list, but instead was ' + typeof itemWithTerm
        );
    }
};

export const testEventsForSubject = (testEvents, subjectBid) => {
    if (testEvents && CommonValues.subjects.find((subject) => subject.get('bid') === subjectBid)) {
        const subjectTestEvents = testEvents.filter((te) => te.get('subject', 0) == subjectBid);
        return subjectTestEvents.isEmpty() ? null : subjectTestEvents;
    } else {
        return null;
    }
};

// Measurement Scale ID
export const testEventsForMsid = (testEvents, msid) => {
    if (testEvents) {
        const msidTestEvents = testEvents.filter((te) => te.get('measurementScaleID', 0) === msid);
        return msidTestEvents.isEmpty() ? null : msidTestEvents;
    } else {
        return null;
    }
};

export const filterSummerTerms = (terms) => {
    // assumes that terms is an Immutable list, please check before calling
    return terms.filter((t) => t.get('termNumber') % 10 !== 3);
};

export const addInstructionalWeekToTerms = (terms, studentInfo) => {
    // assumes terms and studentInfo is not null, please check before calling
    return terms.map((t) => {
        const rosterData = studentInfo.find((si) => si.get('termNumber') === t.get('termNumber'), null);
        return t.set('weeks', rosterData ? rosterData.get('weeksOfInstruction') : t.get('instructionalWeek'));
    });
};

export const convertGrade = (grade) => {
    // handle below 1 and above 12
    if (grade < -1 || grade > 12) {
        return null;
    } else if (grade === 0) {
        return 13; //K
    } else if (grade === -1) {
        return 14; //PK
    }

    return grade;
};

export const findMostRecentStudentInfo = (studentInfo) => {
    if (!studentInfo) {
        throw new Error('studentInfo not populated');
    }
    const mostRecentStudentInfo = studentInfo.maxBy((t) => t.get('termNumber'));

    return {
        grade: mostRecentStudentInfo.get('gradeBid'),
        ay: findAcademicYearForTermNumber(mostRecentStudentInfo.get('termNumber')),
        nweaGradeName: mostRecentStudentInfo.get('gradeNweaName'),
    };
};

export const addGradeToTerms = (terms, studentInfo) => {
    // assumes terms and studentInfo is not null, please check before calling
    const mostRecentAY = findMostRecentStudentInfo(studentInfo);
    return terms.map((t) => {
        const ayForTerm = findAcademicYearForTermNumber(t.get('termNumber'));

        // account for kindergarten and PK in math
        const ayGrade = mostRecentAY.grade > 12 ? (mostRecentAY.grade - 13) * -1 : mostRecentAY.grade;
        const convertedGrade = convertGrade(ayGrade + ayForTerm - mostRecentAY.ay);
        return t
            .set('grade', convertedGrade)
            .set(
                'nweaGradeName',
                convertedGrade > 12 ? CommonValues.nweaGradeNames[convertedGrade] : convertedGrade + ''
            );
    });
};

export const createTermDataForStudent = (terms, studentInfo) => {
    if (!terms || !studentInfo) throw new Error('unexpected condition: must provide terms and studentInfo');

    const termsWithGrades = addGradeToTerms(terms, studentInfo);
    const termsWithGradesAndIWeek = addInstructionalWeekToTerms(termsWithGrades, studentInfo);
    return filterSummerTerms(termsWithGradesAndIWeek).filter((g) => g.get('grade', false)); // filter out null grades
};

export function sortLinkingStudies(p1, p2) {
    if (p1.get('studyType') != p2.get('studyType')) {
        return p1
            .get('studyType')
            .toLowerCase()
            .localeCompare(p2.get('studyType').toLowerCase());
    } else if (p1.get('year') != p2.get('year')) {
        return p2.get('year') - p1.get('year');
    } else {
        return p1
            .get('studyDisplayName')
            .toLowerCase()
            .localeCompare(p2.get('studyDisplayName').toLowerCase());
    }
}

export const findMostRecentTestEventsByMeasurementScaleID = (testEvents) => {
    if (!testEvents || testEvents.isEmpty()) return null;

    return testEvents
        .groupBy((e) => e.get('measurementScaleID'))
        .map((tes, measurementScaleID) => {
            return tes.maxBy((e) => e.get('termNumber'));
        });
};

export const findMostRecentTermNumberByTestEvents = (testEvents) => {
    if (!testEvents || testEvents.isEmpty()) return null;

    return testEvents
        .maxBy((testEvent) => {
            return testEvent.get('termNumber');
        })
        .get('termNumber');
};

export const findUniqueTestKeys = (testEvents) => {
    if (!testEvents || testEvents.isEmpty()) {
        return null;
    }

    return testEvents.groupBy((e) => e.get('testkey')).keySeq();
};

// Added for csmn
export const findTestEventForTermAndMsid = (testEvents, termNumber, msid) => {
    //Assuming testEvent is list
    if (!testEvents || testEvents.isEmpty()) {
        return null;
    }

    return testEvents.find((t) => t.get('termNumber') == termNumber && t.get('measurementScaleID') == msid, null, null);
};

export const flattenLearningStatements = (learningContinuumData) => {
    // learning continuum data comes from the endpoint looking like this:
    // {
    //      goalStructure: {
    //          childGoals: {
    //             <childgoalkey>: {
    //                 childGoal: "Put your whole self in",
    //                 displayOrder: 1,
    //                 goalkey: <parent goal key>
    //             }
    //          },
    //          goals: {
    //             <goalkey>: {
    //                 displayOrder: 1,
    //                 goal: "GIve 117% percent"
    //             }
    //          }
    //      },
    //      learningStatements: {
    //          <lsId>: {
    //              description: "Learning statement text",
    //              sortOrder: 33,
    //              standards: [
    //                  {
    //                      childGoalKey: <childgoalkey>,
    //                      maxRit: 221,
    //                      minRit: 187,
    //                      standardKey: <standardkey>,
    //                      type: "STANDARD_STATEMENT"
    //                  },
    //                  {
    //                      childGoalKey: <childgoalkey>,
    //                      maxRit: 244,
    //                      minRit: 206,
    //                      standardKey: <topickey>,
    //                      type: "TOPIC"
    //                  }
    //              ]
    //          }
    //       },
    //      standardStatements: {
    //          grades: { <gradename>: { displayOrder: 110} },
    //          statements: {
    //            <standardkey>: {
    //                displayOrder: 33,
    //                gradeLevel: <gradename>,
    //                standardkey: <standardkey>,
    //                standardStatementDescription: "really long standard description"
    //            }
    //         }
    //      },
    //      test: { key: <testkey>, learningStatementType: "DESCARTES", maxRit: 278, minRit: 133 },
    //      topics: {
    //          <topickey>: {
    //              description: "the topic description",
    //              displayOrder: 3478
    //          }
    //      }
    // }
    //
    //
    // and will come out of this method looking like:
    //     [
    //          {
    //               goalkey: <goalkey>,
    //               goalName: "Give 117%",
    //               subgoalId: <childgoalkey>,
    //               subgoalName: "Put your whole self in",
    //               group: "topic",
    //               groupSortOrder: 3478,
    //               groupId: <topickey>,
    //               groupDesc: 'the topic description',
    //               lsName: "learning statement texgt",
    //               lsId: <lsId>,
    //               lsSortOrder: 33,
    //               maxRit: 244,
    //               minRit: 206,
    //               grade: null
    //          },
    //          {
    //               goalkey: <goalkey>,
    //               goalName: "Give 117%",
    //               subgoalId: <childgoalkey>,
    //               subgoalName: "Put your whole self in",
    //               group: "standard",
    //               groupSortOrder: 33, // null for descartes
    //               groupId: <standardkey>, // null for descartes
    //               groupDesc: 'really long standard description', // 'descartes'
    //               lsName: "learning statement texgt",
    //               lsId: <lsId>,
    //               lsSortOrder: 33,
    //               maxRit: 221,
    //               minRit: 187,
    //               grade: <gradename>
    //          }
    //     ]
    if (!learningContinuumData || !learningContinuumData.has('learningStatements')) {
        return Immutable.Map();
    }

    let statements = [];
    const lsType = learningContinuumData.getIn(['test', 'learningStatementType'])
        ? learningContinuumData.getIn(['test', 'learningStatementType']).toLowerCase()
        : 'none';

    learningContinuumData.get('learningStatements').forEach((ls, lsId) => {
        ls.get('standards').forEach((s) => {
            const group =
                lsType === StandardTypes.descartes.storeValue
                    ? StandardTypes.descartes.storeValue
                    : s.get('type') === StandardTypes.topic.valueFromEndpoint
                    ? StandardTypes.topic.storeValue
                    : StandardTypes.standard.storeValue;

            const groupId = lsType === StandardTypes.descartes.storeValue ? null : s.get('standardKey');
            const subgoal = learningContinuumData
                .get('goalStructure')
                .get('childGoals')
                .get(`${s.get('childGoalKey')}`);
            const goal = learningContinuumData
                .get('goalStructure')
                .get('goals')
                .get(`${subgoal.get('goalKey')}`);
            const learningStatement = {
                goalkey: subgoal.get('goalKey'),
                goalName: goal.get('goal'),
                subgoalId: s.get('childGoalKey'),
                subgoalName: subgoal.get('childGoal'),
                group,
                groupId,
                lsId,
                lsName: ls.get('description'),
                lsSortOrder: ls.get('sortOrder'),
                maxRit: s.get('maxRit'),
                minRit: s.get('minRit'),
            };

            // add group information
            if (lsType === StandardTypes.descartes.storeValue) {
                learningStatement.groupDesc = StandardTypes.descartes.storeValue;
                learningStatement.groupSortOrder = null;
                learningStatement.grade = null;
                statements.push(learningStatement);
            } else if (group === StandardTypes.topic.storeValue) {
                const topic = learningContinuumData.get('topics').get(`${groupId}`);

                if (!!topic) {
                    learningStatement.groupDesc = topic.get('description');
                    learningStatement.groupSortOrder = topic.get('displayOrder');
                    learningStatement.grade = null;
                    statements.push(learningStatement);
                } else {
                    throw new Error(`could not find topic for groupId: ${groupId}`);
                }
            } else {
                const standard = learningContinuumData
                    .get('standardStatements')
                    .get('statements')
                    .get(`${groupId}`);

                if (!!standard) {
                    learningStatement.groupDesc = standard.get('standardStatementDescription');
                    learningStatement.groupSortOrder = standard.get('displayOrder');
                    learningStatement.grade = standard.get('gradeLevel');
                    statements.push(learningStatement);
                } else {
                    throw new Error(`could not find standard for groupId: ${groupId}`);
                }
            }
        });
    });
    return toImmutable(statements);
};

export const canPerformNextButtonAction = (props, state) => {
    //in pages verify at least one page is selected.
    if (props.batchTab == 'pages' && state.selectedPagesForPrinting.size > 0) {
        return true;
    } else if (props.batchTab === 'class' && props.classSelected) {
        return true;
    }

    //In subjects verify at least one subject is selected.
    else if (props.batchTab == 'subjects' && state.selectedSubjectsForPrinting.size > 0) {
        return true;
    }

    //In students verify at least one student is selected.
    else if (props.batchTab == 'students') {
        const selectedStudents =
            props.selectedStudentForPrinting == 'all'
                ? props.students.size
                : props.selectedStudentForPrinting == 'choose' && !state.studentsChanged
                ? props.students.size
                : props.selectedStudentForPrinting == 'choose' && state.selectedStudents != undefined
                ? state.selectedStudents.size
                : 0;

        if (
            props.selectedStudentForPrinting == 'current' ||
            (selectedStudents != 0 && selectedStudents <= CommonValues.MAX_STUDENTS_FOR_BATCH_PRINT)
        ) {
            return true;
        } else if (selectedStudents > CommonValues.MAX_STUDENTS_FOR_BATCH_PRINT || selectedStudents == 0) {
            return false;
        }
    }

    // In Subjects, Pages or Students & select students option initially selectedPagesForPrinting &
    // selectedSubjectsForPrinting & selectedStudentForPrinting will be empty i.e size =0 & its size changes only
    // after we interact with the page (make any selection).In such scenario, when user doesn't interact
    // with the page will be able to navigate with default selections.
    else if (
        (props.batchTab == 'subjects' && !state.subjectsChanged) ||
        props.batchTab == 'data' ||
        (props.batchTab == 'students' && props.selectedStudentForPrinting == 'choose' && !state.studentsChanged)
    ) {
        return true;
    } else {
        return false;
    }
};

function getClasses(tab, visitedSections) {
    return visitedSections.includes(tab) ? `${tab} visited cursor-not-allowed` : `${tab} cursor-not-allowed`;
}

export function tabClasses(tab, props) {
    let classes;
    const subjects = 'subjects';

    if (props.visitedSections.includes(tab)) {
        classes = `${tab} visited cursor-allowed`;
    } else {
        classes = `${tab} cursor-not-allowed`;
    }

    if (props.batchTab == subjects) {
        if (props.subjectsChanged && props.selectedSubjects.size <= 0) {
            classes = getClasses(tab, props.visitedSections);
        }
    } else if (props.batchTab == 'pages') {
        if (props.pagesChanged && props.selectedPages.size <= 0) {
            classes = getClasses(tab, props.visitedSections);
        }
    } else if (props.batchTab == 'students') {
        if (
            props.selectedStudentForPrinting == 'all' &&
            props.students.size > CommonValues.MAX_STUDENTS_FOR_BATCH_PRINT
        ) {
            classes = getClasses(tab, props.visitedSections);
        } else if (props.selectedStudentForPrinting == 'choose') {
            if (!props.studentsChanged && props.students.size > CommonValues.MAX_STUDENTS_FOR_BATCH_PRINT) {
                classes = getClasses(tab, props.visitedSections);
            } else if (
                props.studentsChanged &&
                (props.selectedStudentsFromList == null ||
                    props.selectedStudentsFromList.size == 0 ||
                    props.selectedStudentsFromList.size > CommonValues.MAX_STUDENTS_FOR_BATCH_PRINT)
            ) {
                classes = props.visitedSections.includes(subjects)
                    ? `${tab} visited cursor-not-allowed`
                    : `${tab} cursor-not-allowed`;
            }
        }
    }
    return classes;
}

export function studentIsLoaded(studentList, studentBid, checkForTextBids = false, logFailure = false) {
    const teLoaded = fieldLoadedForStudent(studentList, studentBid, 'testEvents');
    const studentInfoLoaded = fieldLoadedForStudent(studentList, studentBid, 'studentInfo');
    const termDataLoaded = fieldLoadedForStudent(studentList, studentBid, 'termData');
    const measurementScaleIDsForStudent = Reactor.evaluate(
        StudentGetters.measurementScaleIDsForStudentByBid(studentBid)
    );
    const allMSIDsAndSubjects = DataUtils.combineSubjectsAndMeasurementScaleIDs(measurementScaleIDsForStudent);
    const growthGoalsLoaded = growthGoalsLoadedForStudent(
        studentList,
        studentBid,
        allMSIDsAndSubjects,
        checkForTextBids
    );
    const highlightsDataLoaded = fieldLoadedForStudent(studentList, studentBid, 'highlights');
    const comparisonGrowthPercentiles = fieldLoadedForStudent(studentList, studentBid, 'growthPercentiles');

    if (logFailure) {
        const all = {
            teLoaded,
            studentInfoLoaded,
            termDataLoaded,
            growthGoalsLoaded,
            highlightsDataLoaded,
            comparisonGrowthPercentiles,
        };
        const allLoaded =
            teLoaded && studentInfoLoaded && termDataLoaded && growthGoalsLoaded && comparisonGrowthPercentiles;
        if (!allLoaded) {
            console.warn(`studentBid: ${studentBid}, all: ${JSON.stringify(all, null, '\t')}`);
        } else {
            console.log('ALL LOADED');
        }
    }

    return (
        teLoaded &&
        studentInfoLoaded &&
        termDataLoaded &&
        growthGoalsLoaded &&
        highlightsDataLoaded &&
        comparisonGrowthPercentiles
    );
}

/**
 * Avoid flattenChildren() warning for duplicate records for child key
 *
 * @param originalRecords
 * @param comparisonField
 */
export function getUniqueRecords(originalRecords, comparisonField) {
    let recordMap = new Map();

    if (originalRecords) {
        for (let i = 0; i < originalRecords.size; i++) {
            let originalRecord = originalRecords.get(i);
            let comparisonValue = originalRecord.get(comparisonField);
            if (!recordMap.has(comparisonValue)) {
                recordMap.set(comparisonValue, originalRecord);
            }
        }
    }
    return toImmutable(Array.from(recordMap.values()));
}

export function isUserNormData(testEvent) {
    let normType = testEvent ? testEvent.getIn(['statusNorms', 'normType'], null) : null;

    return normType ? normType.toLowerCase() === 'user' : false;
}

/* This function intakes an array of MeasurementScaleIDs and returns an immutable
 * array of objects with the key 'bid' that represents the supplied
 * measurementScaleIDs combined with all of the subjects declared in CommonValues
 */
export function combineSubjectsAndMeasurementScaleIDs(measurementScaleIDs) {
    if (!Array.isArray(measurementScaleIDs)) {
        return CommonValues.subjects;
    }
    const subjectsToUse = measurementScaleIDs;

    CommonValues.subjects.forEach((s) => {
        if (!subjectsToUse.includes(s.get('bid'))) {
            subjectsToUse.push(s.get('bid'));
        }
    });
    const subjectsButLikeCommonValues = toImmutable(
        subjectsToUse.map((s) => {
            return { bid: s };
        })
    );
    return subjectsButLikeCommonValues;
}

export function removeOverlappingTestEvents(testEvents, overlapTracker) {
    /* The following steps handle the scenarios where multiple test events exist within a single term for the
     * the same "subject" tab. For instance, if an Algebra and Math test exist in the same term.
     * The intent is to find the test event that has the latest test date and keep it, and disregard any other
     * test events for the same subject and term combination.
     */
    const removedTestEvents = [];
    if (Array.isArray(testEvents) && typeof overlapTracker === 'object') {
        Object.keys(overlapTracker).forEach((subjectTerm) => {
            if (overlapTracker[subjectTerm].count > 1) {
                // subjectTerm example: 202102-1
                const termNumber = parseInt(subjectTerm.substr(0, 6), 10);
                const subject = parseInt(subjectTerm.substr(7), 10);
                const latestDate = overlapTracker[subjectTerm].latestDate;
                testEvents = testEvents.filter((testEvent) => {
                    if (
                        testEvent.subject === subject &&
                        testEvent.termNumber === termNumber &&
                        testEvent.testDate !== latestDate
                    ) {
                        removedTestEvents.push(testEvent);
                        return false;
                    } else {
                        return true;
                    }
                });
            }
        });
    }
    return { cleanedTestEvents: testEvents, removedTestEvents };
}

export function trackTestEventOverlaps(testEvent, tracker = {}) {
    /* Tracker object = {
     *     "202102-1": {
     *         count: 2,
     *         latestDate: 1624465536403,
     *     },
     *     "<termNumber>-<subjectBid>": {
     *          count: <number-of-times-key-exists-in-test-event-array>,
     *          latestDate: <most-recent-timestamp-of-matching-test-events>,
     *     }
     * }
     */
    if (!testEvent) {
        return tracker;
    }
    const key = `${testEvent.termNumber}-${testEvent.subject}`;
    if (Object.keys(tracker).includes(key)) {
        // Subject already exists for this term.
        tracker[key].count += 1;
        tracker[key].latestDate =
            testEvent.testDate > tracker[key].latestDate ? testEvent.testDate : tracker[key].latestDate;
    } else {
        tracker[key] = {
            count: 1,
            latestDate: testEvent.testDate,
        };
    }
    return tracker;
}

export default {
    findNextStudent,
    findPreviousStudent,
    combineSubjectsAndMeasurementScaleIDs,
    findTestEventForTermAndMsid,
    addGradeToTerms,
    addInstructionalWeekToTerms,
    buildRitQuintileKeyset,
    buildDistrictAverageKeyset,
    canPerformNextButtonAction,
    convertGrade,
    createTermDataForStudent,
    districtAveragesLoaded,
    fieldLoadedForStudent,
    filterSummerTerms,
    findAdjacentStudent,
    findMissingDistrictAverageKeys,
    findMissingRitQuintileKeys,
    findMostRecentStudentInfo,
    findMostRecentTestEventsBySubject,
    findMostRecentTestEventsByMeasurementScaleID,
    findMostRecentTermNumberByTestEvents,
    findTestEventForTermAndSubject,
    findUniqueTestKeys,
    flattenLearningStatements,
    getUniqueRecords,
    growthGoalsLoadedForStudent,
    growthGoalNormsLoaded,
    isUserNormData,
    latestItemByTerm,
    ritQuintilesLoaded,
    sortLinkingStudies,
    studentIsLoaded,
    tabClasses,
    testEventsForSubject,
    testEventsForMsid,
    removeOverlappingTestEvents,
    trackTestEventOverlaps,
};
